import { useCallback, useMemo } from 'react'
import { getProjectStats, projects, type Project } from '../data/projects'

/**
 * Custom hook for managing project data with optimized filtering and memoization
 *
 * Features:
 * - Memoized project filtering for performance
 * - Pre-calculated statistics
 * - Optimized callback functions
 * - Type-safe returns
 */

export interface UseProjectsOptions {
  selectedCategory: string
  showFeaturedOnly: boolean
}

export interface UseProjectsReturn {
  filteredProjects: Project[]
  allProjects: Project[]
  featuredProjects: Project[]
  stats: {
    totalProjects: number
    platforms: number
    featuredProjects: number
    categories: number
  }
  isEmpty: boolean
}

export function useProjects({
  selectedCategory,
  showFeaturedOnly,
}: UseProjectsOptions): UseProjectsReturn {
  // Memoize expensive filtering operations
  const filteredProjects = useMemo(() => {
    let filtered = projects

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(
        project => project.category === selectedCategory
      )
    }

    // Filter by featured status
    if (showFeaturedOnly) {
      filtered = filtered.filter(project => project.featured)
    }

    return filtered
  }, [selectedCategory, showFeaturedOnly])

  // Memoize featured projects
  const featuredProjects = useMemo(() => {
    return projects.filter(project => project.featured)
  }, [])

  // Memoize statistics
  const stats = useMemo(() => {
    return getProjectStats()
  }, [])

  // Check if results are empty
  const isEmpty = filteredProjects.length === 0

  return {
    filteredProjects,
    allProjects: projects,
    featuredProjects,
    stats,
    isEmpty,
  }
}

/**
 * Hook for optimized project search functionality
 */
export function useProjectSearch(searchTerm: string) {
  return useMemo(() => {
    if (!searchTerm.trim()) {
      return projects
    }

    const term = searchTerm.toLowerCase()
    return projects.filter(
      project =>
        project.title.toLowerCase().includes(term) ||
        project.description.toLowerCase().includes(term) ||
        project.technologies.some(tech => tech.toLowerCase().includes(term)) ||
        project.achievements.some(achievement =>
          achievement.toLowerCase().includes(term)
        )
    )
  }, [searchTerm])
}

/**
 * Hook for project category management
 */
export function useProjectCategories() {
  const getProjectCountByCategory = useCallback((categoryId: string) => {
    if (categoryId === 'all') {
      return projects.length
    }
    return projects.filter(project => project.category === categoryId).length
  }, [])

  const getCategoryLabel = useCallback((categoryId: string) => {
    const categoryMap: Record<string, string> = {
      all: 'All Projects',
      enterprise: 'Enterprise Systems',
      'mobile-games': 'Mobile Games',
      'console-games': 'Console Games',
      'web-development': 'Web Development',
      'personal-projects': 'Personal Projects',
    }
    return categoryMap[categoryId] || categoryId
  }, [])

  return {
    getProjectCountByCategory,
    getCategoryLabel,
  }
}
